import type {
  ApplePayInitPayload,
  ApplePayShippingMethod,
  CartStoreAddress
} from '#types/applePay'
import type { CartState, CartStoreContext, DeliveryOptionsStorage, InvoiceData } from '#types/cart'
import type { Order } from '#types/components/checkout/order'

declare global {
  interface Window {
    ApplePaySession: ApplePaySession
    ApplePayError: ApplePayError
  }
}

export const useApplePay = (storeContext?: CartStoreContext) => {
  const { $t } = useNuxtApp()
  const {
    checkout: { applePay },
    pages: { cart: { coupon: { isAutoPromoCodeEnabled } } }
  } = useAppConfig()
  const { cart: cartApi } = useApi()
  const cart = useCartStore(storeContext)
  const { reset, postPaymentInstruments, getApplicableShippingMethods } = cart
  const {
    basketId,
    currency,
    shippingMethods,
    totals,
    giftCards
  } = storeToRefs(cart)
  const order = useCheckoutStore(storeContext)
  const route = useRoute()
  const { enableShippingAddressRecaptcha } = useFeatureFlags()
  const { load, countries } = useAddressApiRules()

  const deliveryOptions = useSessionStorage('deliveryOptions', { collectionPoint: {}, shipToHome: {} } as DeliveryOptionsStorage)
  const selectedCollectionPoint = computed(() => deliveryOptions.value.collectionPoint)

  const paymentRequestRef = ref<ApplePayJS.ApplePayPaymentRequest>()
  // used for set loading state on Apple Pay button
  const isApplePayActive = ref(false)

  // for temporary cart we need to pass a query parameter to some endpoints. More details here domains/commerce/composables/useApplePay/README.md
  const queryParams = computed(() => storeContext === 'applePayPdp' ? { bid: 'cookie' } : undefined)

  // we need to check if user is on cart page so we don't use the collectionPoint flow if they started it on checkout and went back to cart
  const isCartPage = computed(() => route.name?.toString().includes('cart'))
  // we can't use the collectionPoint flow if user is on cart (in case they've started collection point flow on checkout and went back)
  const isCollectionPoint = computed(() => cart.hasCollectionPointItems && !isCartPage.value)

  const getLineItems = (): ApplePayJS.ApplePayLineItem[] => {
    if (!totals.value)
      return []

    const lineItems: ApplePayJS.ApplePayLineItem[] = [
      {
        label: $t.subtotal,
        amount: totals.value.itemTotal.toString(),
        type: 'final'
      },
      {
        label: $t.shipping,
        amount: totals.value.shipping.toString()
      },
      ...giftCards.value.map((giftCard) => ({
        label: giftCard.c_gcCardNumber ? `${$t.giftCard} - ${maskGiftCard(giftCard.c_gcCardNumber)}` : '',
        amount: `-${giftCard.amount}`
      }))
    ]

    if (totals.value.totalDiscount) {
      lineItems.push({
        label: $t.discountItem,
        amount: `${totals.value.totalDiscount}`
      })
    }
    // we have to show taxes on the FE only if the taxation comes back as 'net' from the BE
    if (totals.value.taxation === 'net') {
      lineItems.push({
        label: $t.taxLabel,
        amount: totals.value.tax.toString()
      })
    }

    return lineItems
  }

  const getPaymentTotal = (): ApplePayJS.ApplePayLineItem => {
    if (!totals.value) {
      return {
        label: '',
        amount: '',
        type: 'final'
      }
    }

    const cartPaymentTotal = totals.value.remainingToPay !== undefined
      ? totals.value.remainingToPay
      : totals.value.total

    return {
      label: $t.total,
      amount: String(cartPaymentTotal),
      type: 'final'
    }
  }

  const getApplePayShippingMethods = () => {
    // no need to add applicable shipping methods for collection points as there is no shipping method selection for this flow
    if (isCollectionPoint.value) return []
    const mapShippingMethod = ({ label, price: { finalAmount, amount }, code, deliveryTime = '' }) => ({
      label,
      amount: finalAmount ?? amount,
      detail: getFormattedDeliveryDate($t.estimatedDelivery, deliveryTime),
      identifier: code,
      selected: shippingMethods.value[0].code === code
    })
    if (cart.shippingMethods[0].applicableShippingMethods.length)
      // don't show pickup or pudo methods even though they are coming from the services https://digital.vfc.com/jira/browse/GLOBAL15-77020
      return cart.shippingMethods[0].applicableShippingMethods.filter((method) => method.code !== 'pickup' && method.code !== 'pudo' && method.code !== 'sts').map(mapShippingMethod)

    return shippingMethods.value.reduce((allMethods, group) => (group.shippingId === 'me'
      ? allMethods.concat(group.applicableShippingMethods.map(mapShippingMethod))
      : []
    ), <ApplePayShippingMethod[]>[])
  }

  // Maps address we're receiving from Apple Pay,
  // to the shape we need in our store for various cart api calls to our services
  const mapAddressData = (address: ApplePayJS.ApplePayPaymentContact = {}) => ([
    {
      addressId: '',
      firstName: address.givenName || '',
      lastName: address.familyName || '',
      email: address.emailAddress || '',
      country: address.countryCode || '',
      addressLine1: address.addressLines?.[0] || '',
      addressLine2: address.addressLines?.[1] || '',
      postalCode: address.postalCode?.toUpperCase() || '',
      city: address.locality || '',
      province: address.administrativeArea?.toUpperCase() || '',
      phone: address.phoneNumber || '',
      stateCode: address.administrativeArea?.toUpperCase() || '',
      countryCode: address.countryCode?.toUpperCase() || ''
    }
  ])

  // Validates if the address provided by the user meets our criteria - Not empty & doesn't contain non-latin characters
  const isAddressValid
    = (address?: ApplePayJS.ApplePayPaymentContact): [boolean, ApplePayJS.ApplePayErrorContactField | undefined] => {
      if (!address) return [false, 'addressLines']

      let errorField: ApplePayJS.ApplePayErrorContactField | undefined
      const mandatoryFields: (keyof ApplePayJS.ApplePayPaymentContact)[]
        = ['addressLines', 'postalCode', 'givenName', 'familyName']

      const areMandatoryFieldsValid = mandatoryFields.every((fieldName) => {
        const fieldValue = address[fieldName]
        // regardless of Apple Pay configuration, incorrect data can be set in system settings
        // and if user does not interact with the form it will be submitted regardless of rules
        // so mandatory fields might not exist in the payload
        if (!fieldValue) {
          errorField = fieldName as ApplePayJS.ApplePayErrorContactField
          return false
        }

        // validate if fields doesn't contain only whitespaces
        if (typeof fieldValue === 'string' && !fieldValue.trim()) {
          errorField = fieldName as ApplePayJS.ApplePayErrorContactField
          return false
        }

        // addressLines are stored in an array and only Line1 is mandatory
        if (Array.isArray(fieldValue) && fieldName === 'addressLines' && !fieldValue[0].trim()) {
          errorField = 'addressLines'
          return false
        }

        return true
      })

      // ensure that we use only allowed characters which are basic latin + the variations used across EMEA region
      const doesContainNonLatinCharacters = Object.entries(address)
        .filter(([fieldName]) => !['phoneNumber', 'emailAddress'].includes(fieldName))
        .some(([fieldName, fieldValue]) => {
          const allowedCharactersRegexp = /^[\wßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿąćčďėęěįłńňœřšťūůųźżž∂\s,.'`\-+@&#*():;<>/\][]+$/i
          if (Array.isArray(fieldValue)) {
            const isInvalid = fieldValue.some((value) => value && !value?.match(allowedCharactersRegexp))
            if (isInvalid) errorField = fieldName as ApplePayJS.ApplePayErrorContactField
            return isInvalid
          }
          const isInvalid = fieldValue && !fieldValue?.match(allowedCharactersRegexp)
          if (isInvalid) errorField = fieldName as ApplePayJS.ApplePayErrorContactField
          return isInvalid
        })

      return [areMandatoryFieldsValid && !doesContainNonLatinCharacters, errorField]
    }

  // This method maps the address the user has entered, or has in the cart store, maps it,
  // so it is in the right shape when it is sent to the initial Apple Pay session payment request
  const mapCartShippingAddressToApplePay = (): ApplePayJS.ApplePayPaymentContact => {
    const cartShippingAddress = shippingMethods.value[0].address

    const addressLines = cartShippingAddress.addressLine1 ? [cartShippingAddress.addressLine1] : []
    if (cartShippingAddress.addressLine2 !== undefined) addressLines.push(cartShippingAddress.addressLine2)

    return {
      phoneNumber: cartShippingAddress.phone || '',
      emailAddress: cartShippingAddress.email || '',
      givenName: cartShippingAddress.firstName || '',
      familyName: cartShippingAddress.lastName || '',
      addressLines,
      locality: cartShippingAddress.city || '',
      postalCode: cartShippingAddress.postalCode || '',
      administrativeArea: cartShippingAddress.province || cartShippingAddress.stateCode || cartShippingAddress.country || '',
      countryCode: cartShippingAddress.countryCode || cartShippingAddress.country || ''
    }
  }

  // Set up the payment request to send to Apple Pay session
  const refreshPaymentRequestWithCurrentCartData = (
    empty: boolean = false,
    options: {
      shippingContact?: ApplePayJS.ApplePayPaymentContact
    } = {}
  ) => {
    paymentRequestRef.value = {
      ...paymentRequestRef.value,
      countryCode: useCountryCode(),
      currencyCode: empty ? useCurrencyCode() : currency.value, // cart.currency
      shippingContactEditingMode: isCollectionPoint.value || options.shippingContact ? 'storePickup' : 'enabled', // Even though the docs state the value "enabled" is deprecated, but 'available' is not yet supported to older devices, so we have to use the old value that still in use and works for all devices. https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingcontacteditingmode,
      lineItems: empty ? undefined : getLineItems(), // subtotal, tax, shipping, totalDiscount
      merchantCapabilities: applePay.merchantCapabilities,
      supportedNetworks: applePay.supportedNetworks,
      requiredBillingContactFields: applePay.requiredBillingContactFields,
      requiredShippingContactFields: applePay.requiredShippingContactFields,
      total: empty ? { label: $t.total, amount: '0' } : getPaymentTotal(), // cart.totals.total
      shippingType: isCollectionPoint.value || options.shippingContact ? 'storePickup' : 'shipping',
      shippingMethods: empty ? undefined : getApplePayShippingMethods(),
      shippingContact: (empty && order.checkoutStep !== 'billing') ? undefined : mapCartShippingAddressToApplePay(),
      ...options
    }
  }

  const clearTemporaryStores = () => {
    // clear and remove only temporary pinia stores
    if (storeContext !== 'applePayPdp') return
    const pinia = usePinia()
    delete pinia.state.value[`cart${storeContext}`]
    delete pinia.state.value[`checkout${storeContext}`]
  }

  // Initialize Apple Pay session
  const initApplePaySession = (payload?: ApplePayInitPayload, invoiceAddress?: InvoiceData) => {
    const recaptcha = useRecaptcha()
    isApplePayActive.value = true

    if (!countries.value.length) load(useCountryCode(), useLocale())

    refreshPaymentRequestWithCurrentCartData(true, payload?.shippingContact
      ? {
          shippingContact: payload.shippingContact
        }
      : {})
    const session = new ApplePaySession(applePay.version, paymentRequestRef.value!)
    const invoiceAddressData = invoiceAddress || {}
    // Payment Option change callback
    session.onpaymentmethodselected = async () => {
      try {
        await getApplicableShippingMethods()

        session.completePaymentMethodSelection({
          newTotal: getPaymentTotal(),
          newLineItems: getLineItems(),
          newShippingMethods: getApplePayShippingMethods()
        })
      }
      catch {
        clearTemporaryStores()
        return session.completeMerchantValidation({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }
    }

    // Session validation callback
    session.onvalidatemerchant = async () => {
      try {
        // add To cart with the PDP context passed through
        if (payload && storeContext) {
          await cart.add({
            product: payload.productId,
            upc: payload.upc,
            addToCartContextKey: storeContext,
            storeId: payload.storeId
          })
          await getApplicableShippingMethods()

          if (payload.storeId) {
            await cart.addShippingAddress([{
              ...cart.shippingMethods[0].address,
              firstName: `${cart.shippingMethods[0].storeName}`,
              lastName: payload.storeId,
              shippingId: cart.shippingMethods[0].shippingId,
              storeId: payload.storeId
            }], recaptcha, 'APPLE_PAY_EXPRESS', 'cookie')
          }

          const promoCode = useSessionStorage('autoPromoCode', '')
          if (isAutoPromoCodeEnabled && promoCode.value) {
            try {
              await cart.applyCoupon(promoCode.value.trim())
            }
            catch {
              // Decision not to interrupt session when promotion is not applied in order to potentially save the sale
              log.error('Coupon could not be applied')
            }
            finally {
              promoCode.value = ''
            }
          }

          refreshPaymentRequestWithCurrentCartData()
        }
        const { data } = await cartApi.getPaymentSession({
          referer: location.host,
          paymentMethodId: 'DW_APPLE_PAY'
        }, {
          headers: {
            'content-type': 'application/json'
          }
        })
        // @ts-expect-error incorrect contract type
        session.completeMerchantValidation(data.value?.meta?.merchantSession)
      }
      catch (e) {
        log.error(e)
        clearTemporaryStores()
        return session.completeMerchantValidation({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }
    }

    // Shipping Address change callback
    session.onshippingcontactselected = async (event) => {
      const countryCode = useCountryCode()
      // check if the country code of the selected shipping address is valid for the application locale
      if (event.shippingContact.countryCode !== countryCode) {
        clearTemporaryStores()
        return session.completeShippingContactSelection({
          newShippingMethods: [],
          newTotal: { label: $t.error, amount: '1', type: 'pending' },
          newLineItems: [],
          errors: [new ApplePayError('shippingContactInvalid', 'country', $t.applePayCountryError)]
        })
      }
      // check if the city name is longer than 50 characters. It would be stuck downstream so we need to throw a message here instead. https://digital.vfc.com/jira/browse/GLOBAL15-91044
      if (event.shippingContact.locality && event.shippingContact.locality.length > 50) {
        return session.completeShippingContactSelection({
          newShippingMethods: [],
          newTotal: { label: $t.error, amount: '1', type: 'pending' },
          newLineItems: [],
          errors: [new ApplePayError('shippingContactInvalid', 'locality', $t.applePayCityNameLength)]
        })
      }

      // Potentially Add address validation via "getProvinceList" call, once it is migrated in https://digital.vfc.com/jira/browse/GLOBAL15-48687
      try {
        const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
        const { totals: newTotals } = await cartApi.$addShippingAddress(basketId.value, {
          ...mapAddressData(event?.shippingContact)[0],
          subscriptions: {
            newsletterConsent: false
          },
          ...captchaResponse && { recaptcha_response: captchaResponse }
        }, { ...queryParams.value, context: 'APPLE_PAY_EXPRESS' })
        // update totals, because the tax amounts from SFCC are coming in this step
        totals.value = newTotals
      }
      catch {
        clearTemporaryStores()

        if (ApplePaySession.supportsVersion(3)) {
          return session.completeShippingContactSelection({
            newShippingMethods: [],
            newTotal: { label: $t.error, amount: '1', type: 'pending' },
            newLineItems: [],
            errors: [new ApplePayError('addressUnserviceable', 'postalAddress', $t.applePayShippingMethodError)]
          })
        }
        else {
          return session.completeShippingContactSelection(
            ApplePaySession.STATUS_FAILURE,
            [],
            { label: $t.error, amount: '1', type: 'pending' },
            []
          )
        }
      }

      await getApplicableShippingMethods()

      session.completeShippingContactSelection({
        newTotal: getPaymentTotal(),
        newLineItems: getLineItems(),
        newShippingMethods: getApplePayShippingMethods()
      })
    }

    // Shipping Method Selected callback
    session.onshippingmethodselected = async (event) => {
      const shippingMethod: ApplePayShippingMethod = event.shippingMethod
      await getApplicableShippingMethods()

      // check if the selected shipping method is in the new list of available shipping methods upon address selection
      const isSelectedAvailable = cart.shippingMethods[0].applicableShippingMethods
        .some((method) => method.code === shippingMethod.identifier)
      if (!isSelectedAvailable) {
        clearTemporaryStores()
        return session.completeShippingMethodSelection({
          // see https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingmethodupdate
          newShippingMethods: getApplePayShippingMethods(),
          newTotal: { label: $t.applePayShippingMethodListError, amount: '1', type: 'pending' },
          newLineItems: [],
          // @ts-expect-error incorrect applepayjs types for "ApplePayShippingMethodUpdate"
          errors: [new ApplePayError('shippingContactInvalid', 'country', $t.applePayCountryError)]
        })
      }
      try {
        const { totals: newTotals } = await cartApi.$setShippingMethod({
          cartId: basketId.value,
          code: shippingMethod.identifier,
          shippingId: 'me',
          shopperAppliedSM: true
        }, { ...queryParams.value, context: isCollectionPoint.value ? 'COLLECTION' : 'APPLE_PAY_EXPRESS' }) as CartState
        // we need to update totals on changing shipping methods because when we are coming from PDP we can't run prepareOrder where this update would happen
        // the information given by BE team for the ticket GLOBAL15-58811
        if (storeContext === 'applePayPdp' && newTotals) totals.value = newTotals
      }
      catch {
        clearTemporaryStores()

        if (ApplePaySession.supportsVersion(3)) {
          return session.completeShippingMethodSelection({
            newShippingMethods: [],
            newTotal: { label: $t.error, amount: '1', type: 'pending' },
            newLineItems: [],
            // @ts-expect-error incorrect applepayjs types for "ApplePayShippingMethodUpdate"
            errors: [new ApplePayError('addressUnserviceable', 'postalAddress', $t.applePayShippingMethodError)]
          })
        }
        else {
          return session.completeShippingMethodSelection(
            ApplePaySession.STATUS_FAILURE,
            { label: $t.applePayShippingMethodListError, amount: '1', type: 'pending' },
            []
          )
        }
      }
      if (storeContext !== 'applePayPdp') await order.prepareOrder()
      const update = {
        newTotal: getPaymentTotal(),
        newLineItems: getLineItems()
      }
      session.completeShippingMethodSelection(update)
    }

    // Payment authorization callback
    session.onpaymentauthorized = async (event) => {
      // Additionally to the validation provided in configuration, we need to check if:
      // 1. fields like AddressLine1, postalCode, name is not just a whitespace
      // 2. fields do not contain non-latin characters e.g. Cyrilic, Chinese, Japanese
      const [isBillingAddressValid, invalidBillingField] = isAddressValid(event?.payment.billingContact)
      if (!isBillingAddressValid) {
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('billingContactInvalid', invalidBillingField)]
        })
      }

      const [isShippingAddressValid, invalidShippingField] = isAddressValid(event?.payment.shippingContact)
      if (!isShippingAddressValid) {
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('shippingContactInvalid', invalidShippingField)]
        })
      }

      // if we have data in session storge around selected collection point and if the collection poing is currently active as method, build the object
      const collectionPointsData = (selectedCollectionPoint.value && isCollectionPoint.value)
        ? {
            storeId: selectedCollectionPoint.value.storeId,
            storeName: selectedCollectionPoint.value.storeName,
            optionType: selectedCollectionPoint.value.optionType,
            bookingCode: selectedCollectionPoint.value.bookingCode
          }
        : {}

      const address = mapAddressData(event?.payment.shippingContact)[0] as CartStoreAddress
      shippingMethods.value[0].address = JSON.parse(JSON.stringify({
        id: null,
        stateCode: null,
        addressLine2: '',
        province: null,
        subscriptions: {
          newsletterConsent: false
        },
        ...address,
        phone: formatE164phone(address.phone, address.phoneCode),
        addressId: address.addressId
      })) as CartStoreAddress

      const getShippingId = (storeId: string) =>
        cart.shippingMethods.find((m) => m.storeId === storeId)?.shippingId

      try {
        const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
        await cartApi.$addShippingAddress(basketId.value, {
          ...mapAddressData(event?.payment.shippingContact)[0],
          subscriptions: {
            newsletterConsent: false
          },
          ...collectionPointsData,
          ...(storeContext === 'applePayPdp' && payload?.storeId && { storeId: payload.storeId, shippingId: getShippingId(payload.storeId) }),
          ...captchaResponse && { recaptcha_response: captchaResponse }
        }, { ...queryParams.value, context: isCollectionPoint.value && selectedCollectionPoint.value?.bookingCode ? 'COLLECTION' : 'APPLE_PAY_EXPRESS' })
      }
      catch (error) {
        console.log('Error adding shipping address', error)
      }

      let isPaymentInstrumentValid = false
      // Check the cart flash massages before making this call
      // Thicket that should define the UX https://digital.vfc.com/jira/browse/GLOBAL15-46952
      try {
        if (storeContext !== 'applePayPdp') await order.prepareOrder()
      }
      catch (err) {
        console.log(err)
        isApplePayActive.value = false
        clearTemporaryStores()
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }

      try {
        if (storeContext !== 'applePayPdp') {
          const paymentInstrumentsResponse: any = await postPaymentInstruments('DW_APPLE_PAY')
          // Get payment instruments and check if Apple Pay is still a valid method
          isPaymentInstrumentValid = !!paymentInstrumentsResponse.payment_instruments?.find(
            (method) => method.payment_method_id === 'DW_APPLE_PAY'
          )
        }
        else {
          isPaymentInstrumentValid = true
        }

        /** Proceed with payment flow if a payment instrument has been validated */
        if (isPaymentInstrumentValid) {
          try {
            const billingAddress = {
              ...mapAddressData({
                ...event?.payment?.billingContact,
                emailAddress: event?.payment?.shippingContact?.emailAddress,
                phoneNumber: event?.payment?.shippingContact?.phoneNumber
              })[0],
              ...invoiceAddressData,
              ...collectionPointsData
            }

            if (!countries.value.some(({ value }) => value === billingAddress.countryCode)) {
              isApplePayActive.value = false
              clearTemporaryStores()
              return session.completePayment({
                status: ApplePaySession.STATUS_FAILURE,
                errors: [new ApplePayError('billingContactInvalid')]
              })
            }

            if (invoiceAddress) await cart.addBillingAddress(billingAddress)
            const captchaResponse = await recaptcha.execute('placeOrder')
            const orderData = await cartApi.$placeOrder({
              recaptcha_response: captchaResponse,
              cartId: basketId.value,
              billingAddress,
              paymentMethod: {
                code: 'DW_APPLE_PAY',
                additionalData: [{
                  sessionID: uuid(),
                  c_applePayToken: JSON.stringify({ payment: { token: event.payment.token } })
                }]
              }
            }, {
              bid: storeContext === 'applePayPdp' ? 'cookie' : undefined
            })
            // Complete the order. Store to the checkout store to use on order confirmation page, then reset cart
            session.completePayment({
              status: ApplePaySession.STATUS_SUCCESS
            })

            try {
              // prepare data for confirmation page
              order.success(orderData as Order)
            }
            finally {
              log.info(`order placed: ${orderData.orderNumber}`)
              isApplePayActive.value = false
              storeContext === 'applePayPdp'
                ? await navigateTo(`/order-confirmation?storeContext=${storeContext}`)
                : await navigateTo('/order-confirmation')
              clearTemporaryStores()
              reset()
            }
          }
          catch {
            isApplePayActive.value = false
            clearTemporaryStores()
            return session.completePayment({
              status: ApplePaySession.STATUS_FAILURE,
              errors: [new ApplePayError('unknown')]
            })
          }
        }
        else {
          // AP Payment is not supported for some reason
          isApplePayActive.value = false
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: [new ApplePayError('unknown')]
          })
          clearTemporaryStores()
        }
      }
      catch {
        // Something went wrong along the way (ex. network issue)
        isApplePayActive.value = false
        clearTemporaryStores()
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }
    }

    session.oncancel = (event) => {
      isApplePayActive.value = false
      clearTemporaryStores()
      console.log(`Apple Pay session canceled: ${JSON.stringify(event)}`)
    }
    // Begin Apple Pay Session
    session.begin()
    return session
  }

  return {
    initApplePaySession,
    isApplePayActive
  }
}
